import type { ComponentPropsWithRef, ReactNode } from "react";

export interface PageHeaderProps
  extends Omit<ComponentPropsWithRef<"header">, "children" | "className"> {
  /** Page identity, metadata, and actions inside the shared padded region. */
  children: ReactNode;
  /** Full-width tabs below the header content. Use TabsList inset for alignment. */
  tabs?: ReactNode;
}

/**
 * Shared page-header chrome for shell routes and standalone pages. Bounds the
 * header height so short viewports retain room for the page body, while keeping
 * tabs visible below independently scrollable header content.
 */
function PageHeader({ children, tabs, ...props }: PageHeaderProps) {
  return (
    <header
      data-slot="page-header"
      {...props}
      className={
        tabs
          ? "shrink-0 flex min-h-0 max-h-[50dvh] flex-col bg-surface"
          : "shrink-0 max-h-[50dvh] overflow-y-auto border-b border-outline-subtle bg-surface p-(--spacing-24)"
      }
    >
      {tabs ? (
        <div className="min-h-0 overflow-y-auto px-(--spacing-24) pt-(--spacing-24) pb-(--spacing-20)">
          {children}
        </div>
      ) : (
        children
      )}
      {tabs && <div className="shrink-0">{tabs}</div>}
    </header>
  );
}

export { PageHeader };
